/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Box, ListItem } from '@mui/material';
import { SpaceNetworkInfo } from '../../../shared/entities/terminal';
import { getSvConnectivityIcon } from '../../../utils/SpaceVehicleUtils';
import appTheme from '../../common/theme/appTheme.json';
import TransportMonitoringPlatformListListItemDataDefault from '../../isanPortal/transportMonitoring/TransportMonitoringPlatformListListItemDataDefault';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    collapseListItem: {
      backgroundColor: '#141D24',
    },
    leftMultiline: {
      marginTop: '0px',
      marginBottom: '0px',
    },
    leftPrimaryActive: {
      color: appTheme.svNetworkIsAvailableTextColor,
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '24px',
    },
    leftPrimaryStandby: {
      color: appTheme.svNetworkIsUnavailableTextColor,
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '24px',
    },
    leftPrimaryOffline: {
      color: appTheme.svNetworkIsOfflineTextColor,
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '24px',
    },
    leftSecondary: {
      color: '#ffffff',
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '16px',
    },
  }),
);

export default function TransportMonitoringCollapseListItem(props: {
  spaceNetworkInfo: SpaceNetworkInfo;
  primaryDatapathUuidInGroup: string;
  index: number;
  total: number;
  terminalTabIndex?: number;
  onRowClickedHandler: (datapathUuid: string, tabIndex: number | undefined) => void;
}) {
  const { spaceNetworkInfo, primaryDatapathUuidInGroup, index, total, terminalTabIndex, onRowClickedHandler } = props;
  const classes = useStyles();

  const connectivityIcon = useMemo(
    () => (
      <Box data-test="terminal-list-item-text__connectivity-icon" width="14px" marginLeft="42px" marginRight="26px">
        {getSvConnectivityIcon(spaceNetworkInfo.networkAvailability)}
      </Box>
    ),
    [spaceNetworkInfo.networkAvailability],
  );
  return (
    <ListItem
      data-test={`platform-list-item__spacecraft-network-list-item__${index}`}
      className={classes.collapseListItem}
      sx={{
        paddingTop: !index ? '13px !important' : '5px !important',
        paddingBottom: index === total - 1 ? '17px !important' : '5px !important',
        backgroundColor: appTheme.primaryBackgroundColor,
      }}
      button
      onClick={(event: React.MouseEvent) => {
        onRowClickedHandler(primaryDatapathUuidInGroup, terminalTabIndex);
      }}
    >
      {connectivityIcon}
      <TransportMonitoringPlatformListListItemDataDefault spaceNetworkInfo={spaceNetworkInfo} classes={classes} />
    </ListItem>
  );
}
